import { styled } from 'styled-components';
import ReactPlayerLib from 'react-player';
import type { SongInfo } from './RecordPlayer';

// Type assertion for react-player v3 compatibility
const ReactPlayer = ReactPlayerLib as any;

type RecordProps = {
    recordSrc: string;
    labelSrc?: string;
    rotationDeg: number;
    songInfo?: SongInfo;
    youtubeVideoId?: string;
    youtubeUrl?: string;
    isPlaying?: boolean;
};

/**
 * Record component that displays a spinning record with optional label image or YouTube player
 * The label has a center hole mask to represent the record's center hole
 */
export function Record({ 
    recordSrc, 
    labelSrc, 
    rotationDeg, 
    songInfo,
    youtubeVideoId,
    youtubeUrl,
    isPlaying = false,
}: RecordProps) {
    const hasYouTube = youtubeVideoId || youtubeUrl;

    return (
        <Container style={{ transform: `rotate(${rotationDeg}deg)` }}>
            {hasYouTube ? (
                <YouTubePlayerWrapper>
                    {/* @ts-ignore - react-player v3 type definitions may be incomplete */}
                    <ReactPlayer
                        url={youtubeUrl || `https://www.youtube.com/watch?v=${youtubeVideoId}`}
                        playing={isPlaying}
                        controls={false}
                        playsinline
                        width="100%"
                        height="100%"
                        onReady={() => console.log('[Record] YouTube player ready')}
                        onError={(e: any) => console.error('[Record] YouTube player error', e)}
                        config={{
                            youtube: {
                                playerVars: {
                                    controls: 0,
                                    modestbranding: 1,
                                    rel: 0,
                                    disablekb: 1,
                                    fs: 0,
                                    playsinline: 1,
                                },
                            } as any,
                        }}
                    />
                </YouTubePlayerWrapper>
            ) : labelSrc ? (
                <LabelImage
                    src={labelSrc}
                    alt="Record label"
                    style={{
                        maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 16%, #000 16%)',
                        WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 16%, #000 16%)',
                    }}
                />
            ) : null}
            <RecordImage src={recordSrc} alt="Record" />
        </Container>
    );
}

const Container = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    transform-origin: center center;

    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    cursor: pointer;
`;

const RecordImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;

    object-fit: contain;
    pointer-events: none;
`;

const LabelImage = styled.img`
    position: absolute;
    inset: 25%;
    width: 50%;
    height: 50%;
    z-index: 0;
    border-radius: 50%;

    object-fit: cover;
    pointer-events: none;
`;

const YouTubePlayerWrapper = styled.div`
    position: absolute;
    inset: 25%;
    width: 50%;
    height: 50%;
    z-index: 0;
    border-radius: 50%;
    overflow: hidden;
    pointer-events: none;

    /* Center hole mask */
    mask-image: radial-gradient(circle at 50% 50%, transparent 0 3%, #000 3%);
    -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 0 3%, #000 3%);

    /* Ensure player fills container */
    & > div {
        width: 100% !important;
        height: 100% !important;
    }

    /* Hide YouTube UI elements */
    iframe {
        pointer-events: none;
    }
`;
